/*
 * Product photo viewer
 * --------------------
 * Tapping (or clicking) a product photo opens a full-screen viewer. The whole photo fits on the
 * screen; shoppers can tap, double-tap, pinch or use the + button to zoom, then swipe (or use the
 * arrows) to reach the next photo.
 *
 * Video and 3D media, and any device where the viewer cannot load, still use the theme's original
 * full-screen modal.
 *
 * The viewer is PhotoSwipe v5 (MIT licence). It is fetched only when someone starts to use the
 * gallery, so it adds nothing to the page load. Files:
 *   photoswipe-lightbox.esm.min.js, photoswipe.esm.min.js, photoswipe.css   vendor files, unmodified
 *   product-lightbox.css                                                    our colours and sizes
 */
(function () {
  var scriptSrc = (document.currentScript && document.currentScript.src) || "";
  var assetBase = "";
  var styleLoads = {};
  var viewerLoad = null;
  var LOAD_TIMEOUT = 8000; // milliseconds to wait for the viewer before using the original modal

  function getAssetBase() {
    if (!assetBase) {
      var src = scriptSrc;
      if (!src) {
        var tag = document.querySelector('script[src*="product-modal.js"]');
        src = tag ? tag.src : "";
      }
      assetBase = src.replace(/product-modal\.js.*$/, "");
    }
    return assetBase;
  }

  function loadStyles(file) {
    var href = getAssetBase() + file;

    if (!styleLoads[href]) {
      styleLoads[href] = new Promise(function (resolve, reject) {
        var link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = function () {
          resolve();
        };
        link.onerror = function () {
          delete styleLoads[href];
          link.remove();
          reject(new Error("Could not load " + file));
        };
        document.head.appendChild(link);
      });
    }

    return styleLoads[href];
  }

  function loadViewer() {
    if (!viewerLoad) {
      var base = getAssetBase();

      viewerLoad = (
        base
          ? Promise.all([
              import(base + "photoswipe-lightbox.esm.min.js"),
              import(base + "photoswipe.esm.min.js"),
              loadStyles("photoswipe.css"),
              loadStyles("product-lightbox.css"),
            ])
          : Promise.reject(new Error("Viewer files not found"))
      ).then(
        function (parts) {
          return { Lightbox: parts[0].default, core: parts[1] };
        },
        function (error) {
          viewerLoad = null; // try again on the next tap
          throw error;
        },
      );
    }

    return viewerLoad;
  }

  // Start fetching the viewer as soon as someone touches or hovers the gallery, so it is ready
  // by the time they tap a photo.
  function warmUp(event) {
    var target = event.target;

    if (target && target.closest && target.closest(".product-media-gallery")) {
      document.removeEventListener("pointerover", warmUp, true);
      document.removeEventListener("pointerdown", warmUp, true);
      loadViewer().catch(function () {});
    }
  }

  document.addEventListener("pointerover", warmUp, { capture: true, passive: true });
  document.addEventListener("pointerdown", warmUp, { capture: true, passive: true });

  if (customElements.get("product-modal")) {
    return;
  }

  customElements.define(
    "product-modal",
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.closeEvent = new CustomEvent("product:modal-close");
        this.openEvent = new CustomEvent("product:modal-open");
        this.viewerBusy = false;
      }

      hide() {
        super.hide();
        document.dispatchEvent(this.closeEvent);
      }

      show(opener) {
        if (this.showViewer(opener)) {
          return;
        }

        this.showModal(opener);
      }

      // The theme's original full-screen modal (used for video, 3D models and as a fallback).
      showModal(opener) {
        super.show(opener);
        this.showActiveMedia();
        document.dispatchEvent(this.openEvent);
      }

      // Photos for the viewer, in gallery order. The modal's own <img> tags are only read for each
      // photo's file address and size.
      getViewerItems() {
        return Array.prototype.map.call(this.querySelectorAll("img[data-media-id]"), function (img) {
          return {
            mediaId: img.getAttribute("data-media-id"),
            src: img.getAttribute("data-viewer-src") || img.currentSrc || img.src,
            width: parseInt(img.getAttribute("data-viewer-width"), 10) || img.width,
            height: parseInt(img.getAttribute("data-viewer-height"), 10) || img.height,
            alt: img.getAttribute("alt") || "",
          };
        });
      }

      // The photo currently shown in the gallery, used as an instant preview while the big one loads.
      getPreviewSrc(mediaId) {
        var images = document.querySelectorAll(
          '.product-media-main[data-media-id="' + mediaId + '"] img:not([data-zoom-lens])',
        );

        for (var i = 0; i < images.length; i++) {
          if (images[i].complete && images[i].naturalWidth) {
            return images[i].currentSrc || images[i].src;
          }
        }

        return "";
      }

      // Returns true when the photo viewer takes over from the original modal.
      showViewer(opener) {
        var mediaId = opener && opener.getAttribute("data-media-id");
        var items = this.getViewerItems();
        var index = items.findIndex(function (item) {
          return item.mediaId === mediaId;
        });

        if (index < 0) {
          return false; // video or 3D model
        }

        if (this.viewerBusy) {
          return true; // already opening
        }

        this.viewerBusy = true;

        var preview = this.getPreviewSrc(mediaId);
        if (preview) {
          items[index].msrc = preview;
        }

        // If the viewer is slow to load or cannot load, the original modal opens instead.
        var settled = false;
        var timer = 0;
        var useModal = () => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            this.viewerBusy = false;
            this.showModal(opener);
          }
        };

        timer = setTimeout(useModal, LOAD_TIMEOUT);

        loadViewer().then(
          (viewer) => {
            if (settled) {
              return; // the original modal is already open
            }

            settled = true;
            clearTimeout(timer);

            try {
              this.openViewer(viewer, items, index, opener);
            } catch (error) {
              this.viewerBusy = false;
              this.showModal(opener);
            }
          },
          useModal,
        );

        return true;
      }

      openViewer(viewer, items, index, opener) {
        var modal = this;

        var lightbox = new viewer.Lightbox({
          dataSource: items,
          pswpModule: function () {
            return Promise.resolve(viewer.core);
          },
          showHideAnimationType: "fade",
          bgOpacity: 1,
          padding: { top: 56, bottom: 16, left: 0, right: 0 },
          wheelToZoom: true,
          imageClickAction: "zoom-or-close",
          tapAction: "zoom",
          doubleTapAction: "zoom",
          // Zoom in 2.5x from the fitted photo (never past the photo's real size).
          secondaryZoomLevel: function (zoom) {
            return Math.min(1, zoom.fit * 2.5);
          },
          preload: [1, 2],
          closeTitle: "Close",
          zoomTitle: "Zoom in or out",
          arrowPrevTitle: "Previous photo",
          arrowNextTitle: "Next photo",
          errorMsg: "This photo could not be loaded.",
        });

        // Lock page scrolling before the viewer measures the screen, so the photo is centred exactly
        // (on computers with scroll bars the page gets wider when the bar disappears).
        lightbox.on("beforeOpen", function () {
          document.body.classList.add("overflow-hidden");
        });

        lightbox.on("openingAnimationStart", function () {
          if (window.pauseAllMedia) {
            window.pauseAllMedia();
          }

          document.dispatchEvent(modal.openEvent);
        });

        lightbox.on("destroy", function () {
          document.body.classList.remove("overflow-hidden");
          modal.viewerBusy = false;
          document.dispatchEvent(modal.closeEvent);
        });

        lightbox.init();

        if (!lightbox.loadAndOpen(index)) {
          this.viewerBusy = false;
          this.showModal(opener);
        }
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`,
        ).forEach((element) => {
          element.classList.remove("is-active");
        });
        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`,
        );
        const activeMediaTemplate = activeMedia.querySelector("template");
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add("is-active");
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == "DEFERRED-MEDIA" &&
          activeMediaContent &&
          activeMediaContent.querySelector(".js-youtube")
        ) {
          activeMedia.loadContent();
        }
      }
    },
  );
})();